import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';

const getOrgPrefix = (orgSlug?: string) => orgSlug ? `/${orgSlug}` : '';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useRundown,
  useUpdateRundown,
  useAddSection,
  useDeleteSection,
  useDeleteRundownItem,
  useUpdateRundownItem,
  useReorderSections,
  useReorderRundownItems,
} from '../../hooks/useRundown';
import { ClaimSelector } from './ClaimSelector';
import { StoryBrowser } from './StoryBrowser';
import { ArrowLeft, Calendar, Edit2, Check, X, Layers, ChevronDown, ChevronRight, Trash2, GripVertical, FileText, Star } from 'lucide-react';
import type { RundownSectionFull, RundownItemFull, RundownStatus, ItemSize } from '@news-assist/shared';

const statusOptions: { value: RundownStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'ready', label: 'Ready' },
  { value: 'recorded', label: 'Recorded' },
  { value: 'published', label: 'Published' },
];

const sizeOptions: { value: ItemSize; label: string }[] = [
  { value: 'headline', label: 'Headline' },
  { value: 'major', label: 'Major' },
  { value: 'normal', label: 'Normal' },
];

const sizeColors: Record<ItemSize, string> = {
  headline: 'bg-amber-100 text-amber-800',
  major: 'bg-blue-100 text-blue-700',
  normal: 'bg-gray-100 text-gray-700',
};

// Sortable Item Component
function SortableItem({
  item,
  isSelected,
  onSelect,
  onDelete,
  onSizeChange,
}: {
  item: RundownItemFull;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onSizeChange: (size: ItemSize) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`
        p-2 rounded-lg cursor-pointer transition-all
        ${isSelected
          ? 'bg-blue-50 border-2 border-blue-400'
          : 'bg-white border border-gray-200 hover:border-gray-300'
        }
      `}
    >
      <div className="flex items-center gap-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3 h-3 text-gray-300" />
        </button>
        <FileText className="w-3 h-3 text-gray-400" />
        <span className="text-sm font-medium text-gray-900 flex-1 truncate">
          {item.story.title}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className="flex items-center gap-2 mt-2 ml-5">
        <select
          value={item.size}
          onChange={(e) => { e.stopPropagation(); onSizeChange(e.target.value as ItemSize); }}
          onClick={(e) => e.stopPropagation()}
          className={`text-xs px-2 py-0.5 rounded-full font-medium border-0 cursor-pointer ${sizeColors[item.size]}`}
        >
          {sizeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <span className="text-xs text-gray-500 ml-auto">
          {item.includedCount} claims
          {item.starredCount > 0 && (
            <span className="text-amber-600 ml-1">
              <Star className="w-3 h-3 inline fill-current" /> {item.starredCount}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// Sortable Section Component
function SortableSection({
  section,
  isExpanded,
  onToggle,
  onDelete,
  selectedItemId,
  onSelectItem,
  onDeleteItem,
  onSizeChange,
  onReorderItems,
}: {
  section: RundownSectionFull;
  isExpanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  selectedItemId: string | null;
  onSelectItem: (id: string) => void;
  onDeleteItem: (id: string) => void;
  onSizeChange: (itemId: string, size: ItemSize) => void;
  onReorderItems: (sectionId: string, itemIds: string[]) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = section.items.findIndex((i) => i.id === active.id);
      const newIndex = section.items.findIndex((i) => i.id === over.id);
      const newOrder = arrayMove(section.items, oldIndex, newIndex);
      onReorderItems(section.id, newOrder.map((i) => i.id));
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg bg-white">
      {/* Section Header */}
      <div
        className="flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-4 h-4 text-gray-300" />
        </button>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
        <Layers className="w-4 h-4 text-purple-500" />
        <span className="font-medium text-gray-900 flex-1">{section.title}</span>
        <span className="text-xs text-gray-500">
          {section.storyCount} {section.storyCount === 1 ? 'story' : 'stories'}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Section Items */}
      {isExpanded && (
        <div className="border-t px-3 pb-3 pt-2 space-y-1 bg-gray-50">
          {section.items.length === 0 ? (
            <p className="text-sm text-gray-400 py-2 text-center">
              No stories - add from right panel
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleItemDragEnd}
            >
              <SortableContext
                items={section.items.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                {section.items.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    isSelected={item.id === selectedItemId}
                    onSelect={() => onSelectItem(item.id)}
                    onDelete={() => onDeleteItem(item.id)}
                    onSizeChange={(size) => onSizeChange(item.id, size)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
}

export function RundownPage() {
  const { id, orgSlug } = useParams<{ id: string; orgSlug: string }>();
  const orgPrefix = getOrgPrefix(orgSlug);
  const { data: rundown, isLoading, error } = useRundown(id || '');
  const updateRundown = useUpdateRundown(id || '');
  const addSection = useAddSection(id || '');
  const deleteSection = useDeleteSection(id || '');
  const deleteItem = useDeleteRundownItem(id || '');
  const updateItem = useUpdateRundownItem(id || '');
  const reorderSections = useReorderSections(id || '');
  const reorderItems = useReorderRundownItems(id || '');

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleTitleSave = async () => {
    if (!editTitle.trim() || editTitle === rundown?.title) {
      setIsEditingTitle(false);
      return;
    }
    await updateRundown.mutateAsync({ title: editTitle });
    setIsEditingTitle(false);
  };

  const handleStatusChange = async (status: RundownStatus) => {
    await updateRundown.mutateAsync({ status });
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (confirm('Delete this section and all its stories?')) {
      await deleteSection.mutateAsync(sectionId);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (confirm('Remove this story from the rundown?')) {
      await deleteItem.mutateAsync(itemId);
      if (selectedItemId === itemId) {
        setSelectedItemId(null);
      }
    }
  };

  const handleSizeChange = (itemId: string, size: ItemSize) => {
    updateItem.mutate({ itemId, size });
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && rundown) {
      const oldIndex = rundown.sections.findIndex((s) => s.id === active.id);
      const newIndex = rundown.sections.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(rundown.sections, oldIndex, newIndex);
      reorderSections.mutate(newOrder.map((s) => s.id));
    }
  };

  const handleReorderItems = (sectionId: string, itemIds: string[]) => {
    reorderItems.mutate({ sectionId, itemIds });
  };

  // Find selected item across all sections
  const selectedItem = rundown?.sections
    .flatMap(s => s.items)
    .find(i => i.id === selectedItemId);

  // Get all story IDs already in rundown
  const existingStoryIds = rundown?.sections.flatMap(s => s.items.map(i => i.storyId)) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading rundown...</div>
      </div>
    );
  }

  if (error || !rundown) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">Failed to load rundown</p>
          <Link to={`${orgPrefix}/rundowns`} className="text-blue-600 hover:underline">
            Back to rundowns
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`${orgPrefix}/rundowns`}
            className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>

          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-semibold"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') setIsEditingTitle(false);
                }}
              />
              <button onClick={handleTitleSave} className="p-1 text-green-600 hover:bg-green-50 rounded">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => setIsEditingTitle(false)} className="p-1 text-gray-500 hover:bg-gray-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setEditTitle(rundown.title); setIsEditingTitle(true); }}
              className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-gray-700"
            >
              {rundown.title}
              <Edit2 className="w-4 h-4 text-gray-400" />
            </button>
          )}

          {rundown.targetDate && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {rundown.targetDate}
            </span>
          )}
        </div>

        <select
          value={rundown.status}
          onChange={(e) => handleStatusChange(e.target.value as RundownStatus)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Rundown Tree */}
        <div className="w-1/2 border-r overflow-y-auto p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Rundown</h2>
            <span className="text-xs text-gray-500">
              {rundown.sectionCount} sections, {rundown.storyCount} stories
            </span>
          </div>

          {rundown.sections.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-200">
              <Layers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No sections yet</p>
              <p className="text-gray-400 text-sm mt-1">Add a stream or create a section from the right panel</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSectionDragEnd}
            >
              <SortableContext
                items={rundown.sections.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {rundown.sections.map((section) => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      isExpanded={expandedSections.has(section.id)}
                      onToggle={() => toggleSection(section.id)}
                      onDelete={() => handleDeleteSection(section.id)}
                      selectedItemId={selectedItemId}
                      onSelectItem={setSelectedItemId}
                      onDeleteItem={handleDeleteItem}
                      onSizeChange={handleSizeChange}
                      onReorderItems={handleReorderItems}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Right: Claim Selector or Story Browser */}
        <div className="w-1/2 h-full overflow-hidden bg-gray-50">
          {selectedItem ? (
            <ClaimSelector
              item={selectedItem}
              rundownId={id!}
              onBack={() => setSelectedItemId(null)}
            />
          ) : (
            <StoryBrowser
              rundownId={id!}
              sections={rundown.sections}
              existingStoryIds={existingStoryIds}
            />
          )}
        </div>
      </div>
    </div>
  );
}
